import { I18n } from 'aws-amplify';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useState, useMemo, useEffect } from 'react';
import '../MCPServerSetting.css';
import { Button, TextField, MenuItem, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { closeTabId, multiPanelAtom, panelInitDataAtom } from '@/state/atom/panelAtom';
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import { updatePanelInitData } from '@/component/multiPanel/panel/handler/PanelHandler';

import {
    EXCLUDED_FIELDS,
    AUTH_METHOD_OPTIONS,
    AUTH_SETTING_FIELD_KEYS,
    getFieldMeta,
    getFieldLabel,
    checkInValidInput,
    handleValidateFormData,
    renderSettingApiStatus,
    openaiConnectorList
} from '../handler/MCPServerUIHandler';

import {
    transformApiDataClient,
    createNewInitApiData,
    handleUpdateMCPServerByAction
} from '../handler/MCPServerLogicHandler';


import RenderAuthSection from './fields/RenderAuthSection';
import RenderMCPSelectField from './fields/RenderMCPSelectField';

import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { useSnackbar } from 'notistack';

EditMCPServer.propTypes = {
    editApiData: PropTypes.object,
    setEditApiData: PropTypes.func,
    setListSettingApi: PropTypes.func,
    listSettingApi: PropTypes.array,
    setIsLoading: PropTypes.func,
    corporationDetail: PropTypes.object,
    projectInuseDetail: PropTypes.object,
    userDetail: PropTypes.object,
    panelType: PropTypes.string,
    props: PropTypes.object,
    handleBackWithReload: PropTypes.func,
};

export default function EditMCPServer({
    editApiData,
    setEditApiData,
    setListSettingApi,
    listSettingApi,
    setIsLoading,
    corporationDetail,
    projectInuseDetail,
    userDetail,
    panelType,
    props,
    handleBackWithReload
}) {
    const initialData = !Utils.isEmptyObject(editApiData) ? editApiData : createNewInitApiData();
    const [formData, setFormData] = useState(initialData);
    const [isEdited, setIsEdited] = useState(false);
    const [subscribeMcpAuth, setSubscribeMcpAuth] = useState(null);
    const [errors, setErrors] = useState({});
    const [isDisplayConfirmDelete, setIsDisplayConfirmDelete] = useState(false);
    const [isDisplayConfirmCancel, setIsDisplayConfirmCancel] = useState(false);
    const [isShowPopUpReload, setIsShowPopUpReload] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const [isShowPopUpCloseTab, setIsShowPopUpCloseTab] = useState(false);
    const [closeTabIdState, setCloseTabIdState] = useRecoilState(closeTabId);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);
    const [panelList, setPanelList] = useRecoilState(multiPanelAtom);
    const [panelInitData, setPanelInitData] = useRecoilState(panelInitDataAtom);

    const isAddNew = useMemo(() => {
        return Utils.getPrefixOfId(formData?.id || '') !== Constant.ID_PREFIX.MCP_SERVER;
    }, [formData?.id]);

    const authedStatus = useMemo(() => {
        const authenStatus = formData?.status || Constant.MCP_SERVER_CONNECT_STATUS.NOT_CONNECT;
        return renderSettingApiStatus(authenStatus);
    }, [formData?.status]);

    const isDisableBtnSave = useMemo(() => {
        const mcpServerType = formData?.type;
        const requiredFieldKeys = [
            Constant.MCP_SERVER_TEXT.SERVER_NAME,
            Constant.MCP_SERVER_TEXT.AUTH_METHOD,
            Constant.MCP_SERVER_TEXT.IS_ACTIVE_CORP,
        ];
        if (mcpServerType === Constant.MCP_SERVER_TYPE.MCP_SERVER) {
            requiredFieldKeys.push(Constant.MCP_SERVER_TEXT.SERVER_URL);
        } 
        if (mcpServerType === Constant.MCP_SERVER_TYPE.OPENAI_CONNECTOR) {
            requiredFieldKeys.push(Constant.MCP_SERVER_TEXT.OPENAI_CONNECTOR);
        } 

        let requiredFieldObject = [];
        if (formData?.authMethod === Constant.AUTH_METHOD_MCP_SERVER.BEARER_SETTING) {
            requiredFieldObject.push({
                authSetting: Constant.AUTH_METHOD_MCP_SERVER.BEARER_SETTING,
                authMethod: Constant.MCP_SERVER_TEXT.BEARER
            })
        }

        if (formData?.authMethod === Constant.AUTH_METHOD_MCP_SERVER.APIKEY_SETTING) {
            requiredFieldObject.push({
                authSetting: Constant.AUTH_METHOD_MCP_SERVER.APIKEY_SETTING,
                authMethod: Constant.MCP_SERVER_TEXT.API_KEY
            })
        }

        if (formData?.authMethod === Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING) {
            const oauth2Fiels = ["authUrl", "clientId", "scope", "secret", "tokenUrl"];
            for (let field of oauth2Fiels) {
                requiredFieldObject.push({
                    authSetting: Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING,
                    authMethod: field
                })
            }
            
        }

        for (const key of requiredFieldKeys) {
            const meta = getFieldMeta(key);
            const value = formData?.[key] ?? '';
            const invalid = checkInValidInput(key, value, meta, null, {});
            const isInvalid = !Utils.isEmptyString(invalid[key]);
            if (isInvalid) return true;
        }

        for (const key of requiredFieldObject) {
            const meta = getFieldMeta(key.authMethod, key.authSetting);
            const value = formData?.[key.authSetting]?.[key.authMethod] ?? '';
            const invalid = checkInValidInput(key.authMethod, value, meta, key.authSetting, {});
            const isInvalid = !Utils.isEmptyString(invalid[key.authSetting]?.[key.authMethod]);
            if (isInvalid) return true;
        }

        return !isEdited;
    }, [isEdited, formData?.serverName, formData?.serverURL, formData?.authMethod, formData?.isActiveCorp, formData?.apiKeySetting, formData?.bearerSetting]);

    // Function Handle Inputs:
    const handleChangeInputValue = useCallback((key, value, parentKey = null) => {
        setIsEdited(true);
        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
         
            if (key === Constant.MCP_SERVER_TEXT.AUTH_METHOD) {
                const oldMethod = formData.authMethod;
                const newMethod = value;

                if (oldMethod && newErrors[oldMethod]) {
                    delete newErrors[oldMethod];
                }

                const initValues = initialData[newMethod] || {};
                const validated = Object.entries(initValues).reduce(
                    (acc, [fieldKey, fieldValue]) => {
                        if (![Constant.MCP_SERVER_TEXT.API_KEY, Constant.MCP_SERVER_TEXT.BEARER].includes(fieldKey)) {
                            const meta = getFieldMeta(fieldKey, newMethod);
                            const err = checkInValidInput(fieldKey, fieldValue, meta, newMethod, {});
                            return { ...acc, ...err };
                        } 
                    },
                    {}
                );

                return {
                    ...newErrors,
                    ...validated
                };
            }

            if (parentKey && newErrors[parentKey]) {
                const { ...restInner } = newErrors[parentKey];
                if (Object.keys(restInner).length === 0) {
                    delete newErrors[parentKey];
                } else {
                    newErrors[parentKey] = restInner;
                }
            } else {
                delete newErrors[key];
            }

            return newErrors;
        });

        setFormData(prev => {
            // Clear nested error
            const newData = { ...prev };

            if (key === Constant.MCP_SERVER_TEXT.AUTH_METHOD) {
                const oldMethod = prev.authMethod;
                const newMethod = value;
                newData.authMethod = newMethod;
                if (initialData[newMethod]) {
                    newData[newMethod] = initialData[newMethod];
                }
                if (oldMethod !== newMethod && newData[oldMethod]) {
                    newData[oldMethod] = initialData[oldMethod];
                }
                return newData;
            }

            if (parentKey) {
                return {
                    ...prev,
                    [parentKey]: {
                        ...prev[parentKey],
                        [key]: value
                    }
                };
            } else {
                return {
                    ...prev,
                    [key]: value
                };
            }
        });
    }, []);

    const handleBlurInputValue = useCallback((key, value, meta, parentKey = null) => {
        setErrors(prevErrors => {
            const invalidValues = checkInValidInput(key, value, meta, parentKey, prevErrors);
            return invalidValues;
        });
    }, []);

    // Function Handle Click Btns:
    const backViewList = useCallback(() => {
        setSubscribeMcpAuth(null);
        setIsEdited(false);
        setEditApiData({});
        setFormData({});
        setErrors({});
        handleBackWithReload();
    }, [handleBackWithReload]);

    const handleResponseError = useCallback((error) => {
        if (error === 'MSG_INVALID_SERVER_NAME_DUPLICATE') {
            setErrors(prev => ({
                ...prev,
                serverName: I18n.get(error)
            }));
        } else if (error === 'COMMSG030') {
            setIsShowPopUpReload(true);
        }
    }, []);

    const getDeleteId = useCallback(() => {
        if (Utils.isEmptyObject(editApiData)) {
            return null;
        }

        // Try editApiData.id first
        let deleteId = editApiData.id;
        const isEditApiDataIdValid = Utils.getPrefixOfId(deleteId || '') === Constant.ID_PREFIX.MCP_SERVER;
        
        if (isEditApiDataIdValid) {
            return deleteId;
        }

        // Try formData.id
        if (formData?.id) {
            const isFormDataIdValid = Utils.getPrefixOfId(formData.id || '') === Constant.ID_PREFIX.MCP_SERVER;
            if (isFormDataIdValid) {
                return formData.id;
            }
        }
        
        // Try to find from listSettingApi by matching serverName and serverURL
        if (!Utils.isEmptyArray(listSettingApi)) {
            const matchedItem = listSettingApi.find(item => 
                item.serverName === (editApiData.serverName || formData?.serverName) &&
                (item.serverURL === (editApiData.serverURL || formData?.serverURL) || 
                 item.openaiConnectorId === (editApiData.openaiConnectorId || formData?.openaiConnectorId))
            );
            if (matchedItem?.id) {
                return matchedItem.id;
            }
        }

        return null;
    }, [editApiData, formData, listSettingApi]);

    const performDelete = useCallback(async (deleteId) => {
        try {
            const response = await handleUpdateMCPServerByAction(
                Constant.MCP_SERVER_UPDATE_ACTION_TYPE.DELETE_BY_ID,
                { id: deleteId },
                {
                    corporationInuse: corporationDetail.id,
                    projectInuse: projectInuseDetail.id,
                    userId: userDetail.id,
                    panelType
                }
            );

            const { error } = response;
            if (!Utils.isEmptyString(error)) {
                handleResponseError(error);
                return;
            }

            enqueueSnackbar(I18n.get('MSG-CM-DELETE-MCP-SERVER-SUCCESSFULLY'), {
                variant: 'success',
                autoHideDuration: 3000
            });
            setListSettingApi(prev => prev.filter(item => item.id !== deleteId));
            backViewList();
        } catch (err) {
            handleResponseError('COMMSG030');
        } finally {
            setIsLoading(false);
        }
    }, [
        handleUpdateMCPServerByAction,
        corporationDetail.id,
        projectInuseDetail.id,
        userDetail.id,
        panelType
    ]);

    const handleConfirmDelete = useCallback(async () => {
        if (Utils.isEmptyObject(editApiData)) {
            setIsDisplayConfirmDelete(false);
            return;
        }

        const deleteId = getDeleteId();
        if (!deleteId) {
            setIsDisplayConfirmDelete(false);
            setIsLoading(false);
            handleResponseError('COMMSG030');
            return;
        }

        setIsDisplayConfirmDelete(false);
        setIsLoading(true);
        await performDelete(deleteId);
    }, [editApiData, getDeleteId, performDelete, handleResponseError, setIsDisplayConfirmDelete, setIsLoading]);

    const updateItemInList = (prev, newData) =>
        prev.map(item => (item.id === newData.id ? newData : item));

    const handleSaveFormData = () => {
        setIsLoading(true);
        localStorageUtil.deleteOauth2LastAuthen();

        const actionType = isAddNew
            ? Constant.MCP_SERVER_UPDATE_ACTION_TYPE.CREATE_NEW
            : Constant.MCP_SERVER_UPDATE_ACTION_TYPE.UPDATE_BY_ID;

        handleUpdateMCPServerByAction(actionType, formData, {
            corporationInuse: corporationDetail.id,
            projectInuse: projectInuseDetail.id,
            userId: userDetail.id,
            panelType
        })
            .then(response => {
                setIsEdited(false);
                const { payload, error } = response;

                if (!Utils.isEmptyString(error)) {
                    handleResponseError(error);
                    return;
                }

                const data = Utils.convertJSONDataToObject(payload);
                enqueueSnackbar(I18n.get('MSG_MCP_SERVER_SAVE_SUCCESS'), {
                    variant: 'success',
                    autoHideDuration: 3000
                });
                backViewList();
            })
            .catch(err => {
                console.log('handleSaveFormData err: ', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };


    const handleRunAuth = async () => {
        setIsLoading(true);
        handleUpdateMCPServerByAction(
            Constant.MCP_SERVER_UPDATE_ACTION_TYPE.RUN_AUTH,
            formData,
            {
                corporationInuse: corporationDetail.id,
                projectInuse: projectInuseDetail.id,
                userId: userDetail.id,
                panelType
            }
        )
            .then(response => {
                let authMethod = formData.authMethod;
                if (![Constant.AUTH_METHOD_MCP_SERVER.NONE_SETTING, Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING].includes(authMethod)) {
                    setFormData(response);
                }
            })
            .catch(err => {
                console.log('ERROR _ handleClickRunTest err: ', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };



    const handleTestConnect = async () => {
        const validated = handleValidateFormData(formData);
        if (validated?.isErrors) {
            setErrors(pre => ({...pre, ...validated?.errors}));
            return;
        }

        setIsLoading(true);
        setIsEdited(true);
        const isCreateNew = formData.id.includes('createNew_');
        let authData = {...formData};

        const actionType = isCreateNew
            ? Constant.MCP_SERVER_UPDATE_ACTION_TYPE.CREATE_NEW
            : Constant.MCP_SERVER_UPDATE_ACTION_TYPE.UPDATE_BY_ID;

        let responseData = await handleUpdateMCPServerByAction(actionType, formData, {
            corporationInuse: corporationDetail.id,
            projectInuse: projectInuseDetail.id,
            userId: userDetail.id,
            panelType
        })
        if (!responseData) {
            setIsLoading(false);
            return;
        }
        setIsEdited(false);
        const { payload, error } = responseData;

        if (!Utils.isEmptyString(error)) {
            handleResponseError(error);
            setIsLoading(false);
            return;
        }

        let data = Utils.convertJSONDataToObject(payload);
        setListSettingApi(prev => [data, ...prev]);
        authData = transformApiDataClient(data);

        handleUpdateMCPServerByAction(
            Constant.MCP_SERVER_UPDATE_ACTION_TYPE.RUN_TEST,
            authData,
            {
                corporationInuse: corporationDetail.id,
                projectInuse: projectInuseDetail.id,
                userId: userDetail.id,
                panelType
            }
        )
            .then(response => {
                let authMethod = formData.authMethod.toUpperCase();
                let resultData = Utils.convertJSONDataToObject(response?.payload);
                let dataResponse = { ...resultData };
                dataResponse.testResult = resultData.testResult;
                dataResponse.isActiveCorp = formData.isActiveCorp;
                if (authMethod === Constant.AUTH_METHOD_MCP_SERVER.NONE_SETTING) {
                    if (resultData.status === Constant.MCP_SERVER_CONNECT_STATUS.SUCCESS) {
                        dataResponse.status = resultData.status;
                    } else {
                        dataResponse.status = Constant.MCP_SERVER_CONNECT_STATUS.FAIL;
                    }
                }
                if (dataResponse.status === Constant.MCP_SERVER_CONNECT_STATUS.FAIL) {
                    enqueueSnackbar(I18n.get('MSG_MCP_SERVER_CONNECT_FAIL'), {
                        variant: 'error',
                        autoHideDuration: 3000
                    });
                }
                const transformed = transformApiDataClient(dataResponse);
                setFormData(transformed);
            })
            .catch(err => {
                console.log('ERROR _ handleTestConnect err: ', err);
                // enqueueSnackbar(I18n.get('MSG_MCP_SERVER_CONNECT_FAIL'), {
                //     variant: 'error',
                //     autoHideDuration: 3000
                // });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    
    useEffect(() => {
        if (props.tabId === closeTabIdState) {
            if (isEdited) {
                setIsShowPopUpCloseTab(true);
            } else {
                let currentPanelInfo = Utils.getPanelInfo(props.panelId, panelList);
                if (!currentPanelInfo || Utils.isEmptyObject(currentPanelInfo)) return;
                closePanelTab({
                    event: null,
                    panelType: currentPanelInfo.type,
                    panelId: props.panelId,
                    tabId: props.tabId
                });
            }
            setCloseTabIdState(0);
        }
    }, [closeTabIdState]);

    useEffect(() => {
        updatePanelInitData(
            props.panelId,
            props.tabId,
            panelInitData,
            setPanelInitData,
            'isEditedData',
            isEdited
        );
    }, [isEdited]);

    const handleAuth = async () => {
        const validated = handleValidateFormData(formData);
        if (validated?.isErrors) {
            setErrors(validated?.errors);
            return;
        }
        const isCreateNew = formData.id.includes('createNew_');
        let authData = {...formData};

            setIsLoading(true);
            const actionType = isCreateNew
                ? Constant.MCP_SERVER_UPDATE_ACTION_TYPE.CREATE_NEW
                : Constant.MCP_SERVER_UPDATE_ACTION_TYPE.UPDATE_BY_ID;

            let response = await handleUpdateMCPServerByAction(actionType, formData, {
                corporationInuse: corporationDetail.id,
                projectInuse: projectInuseDetail.id,
                userId: userDetail.id,
                panelType
            })
            if (!response) {
                setIsLoading(false);
                return;
            }
            setIsEdited(false);
            const { payload, error } = response;

            if (!Utils.isEmptyString(error)) {
                handleResponseError(error);
                setIsLoading(false);
                return;
            }

            let data = Utils.convertJSONDataToObject(payload);
            setListSettingApi(prev => [data, ...prev]);
            authData = transformApiDataClient(data);
            setFormData(authData);
            setIsLoading(false);

        let authMethod = authData.authMethod;
        let resultAuthen = '';
    
        switch (authMethod) {
            case 'oauth2Setting':
                return authByOAuth2(authData);
            default:
                break;
        }
        return resultAuthen;
    };

    const authByOAuth2 = (mcpData) => {
        // redirect authen
        const oauth2Setting = mcpData.oauth2Setting;
        const authUrl = oauth2Setting.authUrl;
        const clientId = oauth2Setting.clientId;
        let redirectUri = `${process.env.REACT_APP_DOMAIN_NAME}oauth2callback`;
        // const redirectUri = `http://localhost:3000/oauth2callback`
        let scope = oauth2Setting.scope;
        if (Array.isArray(scope)) {
            scope = scope
                .map(s => (typeof s === 'string' ? s : s?.value))
                .filter(Boolean)
                .join(' ');
        } else if (typeof scope === 'string') {
            // Normalize comma or multi-space separated strings to single-space delimited
            scope = scope
                .split(/[\s,]+/)
                .filter(Boolean)
                .join(' ');
        }
    
        const state = btoa(JSON.stringify({mcpId: mcpData.id}));
        const authUrlByOAuth2 = `${authUrl}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent&state=${state}`;

        const popup = window.open(
            authUrlByOAuth2,
            'oauth2AuthWindow',
            'noopener,noreferrer,width=700,height=800,left=100,top=50,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no'
        );
        if (popup && typeof popup.focus === 'function') {
            popup.focus();
        }

        setSubscribeMcpAuth(mcpData.id);
    };

    useEffect(() => {
        if (!subscribeMcpAuth) return;

        const handleStorage = (event) => {
            if (event.key === "OAUTH2_AUTH_RESULT") {
                const data = JSON.parse(event.newValue);
                if (data.mcpId === subscribeMcpAuth) {
                    const authStatus = data.success;
                    setFormData(prev => {
                        const newData = { ...prev };
                        newData.status = authStatus ? Constant.MCP_SERVER_CONNECT_STATUS.SUCCESS : Constant.MCP_SERVER_CONNECT_STATUS.FAIL;
                        setListSettingApi(preList => updateItemInList(preList, newData));
                        return newData;
                    });
                    // localStorage.removeItem("OAUTH2_AUTH_RESULT");
                    
                }
                setSubscribeMcpAuth(null);
            }
        };

        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [subscribeMcpAuth]);
    

    const handleChangeActiveCorp = useCallback((e) => {
        setIsEdited(true);
        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
         
            delete newErrors.isActiveCorp;

            return newErrors;
        });
        setFormData(prev => {
            const newData = { ...prev };
            newData.isActiveCorp = e.target.value;

            return newData;
        });
    }, [])

    const getFieldKeyValue = (fieldKey) => {
        return formData[fieldKey]
    }


    const isHiddenFieldKey = (fieldKey) => {
        if (EXCLUDED_FIELDS.includes(fieldKey)) {
            return true;
        }
        if (AUTH_SETTING_FIELD_KEYS.includes(fieldKey)) {
            return true;
        }
        if (fieldKey === 'serverURL' && getFieldKeyValue('type') !== 'MCP_SERVER') {
            return true;
        }
        if (fieldKey === 'openaiConnectorId' && getFieldKeyValue('type') !== 'OPENAI_CONNECTOR') {
            return true;
        }

        return false;
    }


    const getOptionsList = (fieldKey) => {
        const optionList = [
            { label: 'MCPサーバー', value: Constant.MCP_SERVER_TYPE.MCP_SERVER},
            { label: 'コネクタ', value: Constant.MCP_SERVER_TYPE.OPENAI_CONNECTOR },
        ];

   
        switch (fieldKey) {
            case 'type':
                return optionList;
            case 'openaiConnectorId':
                return openaiConnectorList;
        
            default:
                break;
        }
    }


    const selectFieldList = ['type', 'openaiConnectorId']

    return (
        <div style={{ height: '100%' }}>
            {/* Header */}
            <div className="mcp-server-header">
                <div className="mcp-server-header-inner mcp-server-line-space">
                    {/* edit authed status */}
                    {authedStatus}

                    {/* Control Btns */}
                    <div className="edit-mcp-server-btns">
                        <Button
                            className="btn-hg32 btn-light edit-mcp-server-btn"
                            color="primary"
                            variant="contained"
                            onClick={() => {
                                if (isEdited) {
                                    setIsDisplayConfirmCancel(true);
                                    return;
                                }
                                backViewList();
                                localStorageUtil.deleteOauth2LastAuthen()
                            }}
                        >
                            {I18n.get('CM-BUTTON-CANCEL')}
                        </Button>

                        {!isAddNew && (
                            <Button
                                className="btn-delete btn-hg32 edit-mcp-server-btn"
                                variant="contained"
                                onClick={() => {
                                    setIsDisplayConfirmDelete(true)
                                    localStorageUtil.deleteOauth2LastAuthen()
                                }}
                            >
                                {I18n.get('CM-BUTTON-DELETE')}
                            </Button>
                        )}

                        <Button
                            className="btn-hg32 btn-primary edit-mcp-server-btn"
                            color="primary"
                            variant="contained"
                            onClick={handleSaveFormData}
                            disabled={isDisableBtnSave}
                        >
                            {I18n.get('CM-BUTTON-SAVE')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="mcp-server-body">
                <div className="mcp-server-list">
                    {!Utils.isEmptyObject(formData) &&
                        Object.entries(formData).map(([key, value]) => {
                            if (isHiddenFieldKey(key)) return null;

                            // select field: mcpType, connector
                            if (selectFieldList.includes(key)) {
                                return (
                                    <RenderMCPSelectField
                                        key={key}
                                        fieldKey={key}
                                        fieldValue={value}
                                        errors={errors}
                                        formData={formData}
                                        onChange={handleChangeInputValue}
                                        onBlur={handleBlurInputValue}
                                        optionList={getOptionsList(key)}
                                    />
                                );
                            }
                            if (key === Constant.MCP_SERVER_TEXT.AUTH_METHOD)
                                return (
                                    <RenderAuthSection
                                        key={key}
                                        fieldKey={key}
                                        fieldValue={value}
                                        errors={errors}
                                        authData={formData[value]}
                                        formData={formData}
                                        onChange={handleChangeInputValue}
                                        onBlur={handleBlurInputValue}
                                        // getFieldKeyValue={getFieldKeyValue}
                                        RenderField={RenderField}
                                        initialData={initialData}
                                    />
                                );
                            if (key === Constant.MCP_SERVER_TEXT.IS_ACTIVE_CORP) 
                                return (
                                    <RenderRadioField
                                        key={key}
                                        fieldKey={key}
                                        fieldValue={value}
                                        errorMsg={errors[key] || ''}
                                        onChange={handleChangeActiveCorp}
                                        onBlur={handleBlurInputValue}
                                    />
                                )

                            return (
                                <RenderField
                                    key={key}
                                    fieldKey={key}
                                    fieldValue={value}
                                    errorMsg={errors[key] || ''}
                                    onChange={handleChangeInputValue}
                                    onBlur={handleBlurInputValue}
                                    getFieldKeyValue={getFieldKeyValue}
                                />
                            );
                        })}

                    <div className="api-field-wrapper group-btn-connect">
                     
                        {formData?.type === Constant.MCP_SERVER_TYPE.OPENAI_CONNECTOR && 
                            <Button
                                className="btn-hg32 btn-primary api-btn-run-auth"
                                color="primary"
                                variant="contained"
                                onClick={handleAuth}
                            >
                                {I18n.get('CM-BUTTON-AUTHEN')}
                            </Button>
                        } 

                        {formData?.type === Constant.MCP_SERVER_TYPE.MCP_SERVER && 
                            <Button
                                className="btn-hg32 btn-primary api-btn-run-auth"
                                color="primary"
                                variant="contained"
                                onClick={handleTestConnect}
                            >
                                {I18n.get('CM-BUTTON-CONNECT')}
                            </Button>
                        }

                        
                    </div>
                    
                    <RenderField
                        key={Constant.MCP_SERVER_TEXT.MEMO}
                        fieldKey={Constant.MCP_SERVER_TEXT.MEMO}
                        fieldValue={formData?.memo}
                        errorMsg={errors?.memo || ''}
                        onChange={handleChangeInputValue}
                        onBlur={handleBlurInputValue}
                    />
                </div>
            </div>

            {/* Confirm */}
            <ConfirmDialog
                isOpen={isDisplayConfirmDelete}
                msgConfirm={I18n.get('MSG-CM-CONFIRM-DELETE-MCP-SERVER')}
                closeCallBack={() => setIsDisplayConfirmDelete(false)}
                okCallBack={async () => await handleConfirmDelete()}
                textClose={I18n.get('CM-BUTTON-CANCEL')}
                textSubmit={I18n.get('CM-BUTTON-OK')}
            />
            <ConfirmDialog
                isOpen={isShowPopUpReload}
                msgConfirm={I18n.get('COMMSG030')}
                okCallBack={() => window.location.reload()}
            />
            <ConfirmDialog
                isOpen={isDisplayConfirmCancel || isShowPopUpCloseTab}
                msgConfirm={I18n.get('USER12MSG010')}
                closeCallBack={() => {
                    if (isDisplayConfirmCancel) setIsDisplayConfirmCancel(false);
                    if (isShowPopUpCloseTab) setIsShowPopUpCloseTab(false);
                }}
                okCallBack={() => backViewList()}
                textClose={I18n.get('CM-BUTTON-CANCEL')}
                textSubmit={I18n.get('CM-BUTTON-OK')}
            />
        </div>
    );
}



const RenderField = memo(
    ({ fieldKey, fieldValue, errorMsg, onChange, onBlur, parentKey = null }) => {
        const meta = getFieldMeta(fieldKey);
        const inputType = meta.type || 'text';
        const isRequired = meta.required || false;
        const multiline = meta.multiline;
        const readonly = meta.readonly;
        const isDisplayRequired =
            !['inputParamDesc', 'outputFormat'].includes(fieldKey) && isRequired;
        const isNotHandleBlurFields = [
            'apiOverview',
            'inputParamDesc',
            'outputFormat',
            'testURL',
            'testParam',
            'testResult'
        ];

        

        return (
            <div className="api-field-wrapper">
                <h4 className="api-field-label">
                    {getFieldLabel(fieldKey)}{' '}
                    {isDisplayRequired && <span style={{ color: 'red' }}>*</span>}
                </h4>
                <TextField
                    fullWidth
                    variant="outlined"
                    className={`form-control-wrapInput ${
                        multiline ? 'api-field-input-textarea' : 'api-field-input'
                    }`}
                    spellCheck={false}
                    multiline={multiline}
                    minRows={multiline ? 6 : null}
                    maxRows={multiline ? 8 : null}
                    type={inputType}
                    value={fieldValue ?? ''}
                    error={!Utils.isEmptyString(errorMsg)}
                    helperText={errorMsg}
                    InputProps={{
                        readOnly: readonly
                    }}
                    sx={{
                        input: {
                            caretColor: readonly ? 'transparent' : 'default'
                        }
                    }}
                    onChange={e => {
                        if (readonly) return;
                        onChange(fieldKey, e.target.value, parentKey);
                    }}
                    onBlur={e => {
                        if (isNotHandleBlurFields.includes(fieldKey)) return;
                        onBlur(fieldKey, e.target.value, meta, parentKey);
                    }}
                />
            </div>
        );
    }
);
RenderField.propTypes = {
    fieldKey: PropTypes.any,
    fieldValue: PropTypes.any,
    errorMsg: PropTypes.any,
    onChange: PropTypes.any,
    onBlur: PropTypes.any,
    parentKey: PropTypes.any
};

const RenderRadioField = memo(
    ({ fieldKey, fieldValue, errorMsg, onChange, onBlur, parentKey = null }) => {
        const meta = getFieldMeta(fieldKey);
        const isRequired = meta.required || false;
        const isDisplayRequired =
            !['inputParamDesc', 'outputFormat'].includes(fieldKey) && isRequired;

        return (
            <div className="api-field-wrapper">
                <h4 className="api-field-label">
                    {getFieldLabel(fieldKey)}{' '}
                    {isDisplayRequired && <span style={{ color: 'red' }}>*</span>}
                </h4>
                <RadioGroup
                    id="radio-buttons-group-search-method"
                    key={`row-radio-buttons-group-search-method`}
                    row
                    aria-labelledby="radio-buttons-group-label-method"
                    name="row-radio-buttons-group-search-method"
                    value={fieldValue.toString()}
                    onChange={e => onChange(e)}
                    className={`mcp-server-radio`}
                >
                    <div className="searchOption-radio">
                        <FormControlLabel
                            className="custom-radio"
                            value={Constant.MCP_SERVER_ACTIVE_DATA.OFF}
                            control={<Radio size="small" />}
                            label={I18n.get('OFF')}
                        />
                        <FormControlLabel
                            className="custom-radio"
                            value={Constant.MCP_SERVER_ACTIVE_DATA.ON}
                            control={<Radio size="small" />}
                            label={I18n.get('ON')}
                        />
                    </div>
                </RadioGroup>
                {!Utils.isEmptyString(errorMsg) && (
                    <div>
                        <span className='text-warning-chat-ai text-warning-model'>
                            {I18n.get(errorMsg)}
                        </span>
                    </div>
                )}
            </div>
        );
    }
);
RenderRadioField.propTypes = {
    fieldKey: PropTypes.any,
    fieldValue: PropTypes.any,
    errorMsg: PropTypes.any,
    onChange: PropTypes.any,
    onBlur: PropTypes.any,
    parentKey: PropTypes.any
};
